/**
 * PropertyInfo flow DTO + completeness predicate (PF v2 native module — Wave 1).
 *
 * The module's durable state IS this DTO, stored at
 * `productConfigs["${cartItemId}|property_info"]` (MODULE-CONTRACT Pattern A). The
 * wire shape is `AuthoritiesDocumentDTO`, shared verbatim with the legacy flow —
 * so drafts serialized under the old `PropertyInfoAdapter` hydrate here with zero
 * migration and `PF_VERSION` stays frozen at 3.
 *
 * IMPORTANT (spec OQ-1): the shipped adapter's `serialize()` returned a bare
 * `AuthoritiesDocumentDTO` (`getAuthoritiesDocumentRequestData()`), NOT the
 * contract open-Q4 `PropertyInfoFullDTO` composite. Wire-compat is defined by the
 * on-disk bytes, so the native DTO MUST be `AuthoritiesDocumentDTO`. The
 * `property` half (title/type/coords) lives in the v2 `propertySlice`.
 *
 * ZERO React / store imports — `modules/completeness.ts` (which `store/selectors.ts`
 * imports) pulls `propertyInfoIsComplete` from here, so this file must stay pure.
 */

import type {
  AuthoritiesDocumentDTO,
  LandRegistrationSheetDTO
} from 'models/purchaseFlow/AuthoritiesDocumentDTO'

/** The persisted/wire shape — unchanged from the legacy adapter. */
export type PropertyInfoDraftDTO = AuthoritiesDocumentDTO

/**
 * Hierarchical parcel structure emitted by the DocEstate map viewer
 * (`district → section/hallway → parcel numbers`). Lives here (a pure types
 * home) so both the ops hook (`setSheetsFromMap`) and the lifted map component
 * import it without a circular dependency.
 */
export type { GroupedParcels } from 'components/common/ParcelViewerMap/parcel.types'

/**
 * One blank sheet with one blank floor + parcel — the read-boundary default when
 * the persisted DTO has no sheets. NEVER written to the store on mount
 * (read-only-mount, MODULE-CONTRACT §3); it is synthesized for display only and
 * promoted into the DTO on the first user edit (A-09 analogue).
 */
export const makeBlankSheet = (): LandRegistrationSheetDTO => ({
  name: '',
  district: '',
  floors: [{ hallway: '', parcels: [{ number: '' }] }],
})

/**
 * Read-time fallback for controlled inputs. NEVER written to the store on mount.
 * `landRegistrationSheets` stays empty here — the ops hook's read boundary
 * synthesizes one blank sheet for the UI (see `usePropertyInfoConfig`).
 * `dataSource` / `searchLandRegistrationDetails` are omitted — set on interaction
 * only.
 */
export const PROPERTY_INFO_DEFAULT: PropertyInfoDraftDTO = {
  landRegistrationSheets: [],
  landLordInformation: undefined,
  comment: '',
}

/**
 * Deep-default a (possibly partial) resumed sheet. The top-level read-boundary
 * spread is SHALLOW (MODULE-CONTRACT §2 caveat): a resumed sheet whose shape is
 * `{ district: 'D' }` (no `floors`) would crash lifted JSX doing
 * `sheet.floors.map(...)`. This fills `floors` / `parcels` so the UI always sees
 * a fully-shaped tree. It is the nested analogue of measurement's
 * `{ ...DEFAULT, ...raw }`.
 */
export const normalizeSheet = (
  raw: Partial<LandRegistrationSheetDTO> | undefined
): LandRegistrationSheetDTO => {
  const floors = raw?.floors
  return {
    name: raw?.name ?? '',
    district: raw?.district ?? '',
    floors: floors?.length
      ? floors.map((floor) => ({
        hallway: floor?.hallway ?? '',
        parcels: floor?.parcels?.length
          ? floor.parcels.map((parcel) => ({ number: parcel?.number ?? '' }))
          : [{ number: '' }],
      }))
      : [{ hallway: '', parcels: [{ number: '' }] }],
  }
}

/**
 * The stored DTO → the `AUTHORITIES_DOCUMENT` wire request.
 *
 * One rule, and it is legacy's (`PropertyInformationStep.context.ts:275`): when
 * BKBN is doing the lookup, the sheets are sent EMPTY. Whatever the client typed
 * before ticking the card is theirs to keep in the draft, but it is not what the
 * order asks for — sending both would tell the authority to use half-entered data
 * we were paid to find.
 *
 * Lives here rather than in the serializer because the serializer does not yet
 * emit an `AUTHORITIES_DOCUMENT` configuration at all; when it does, it calls
 * this rather than re-deriving the rule.
 */
export const toAuthoritiesDocumentRequest = (
  dto: PropertyInfoDraftDTO | undefined
): PropertyInfoDraftDTO => ({
  ...PROPERTY_INFO_DEFAULT,
  ...dto,
  landRegistrationSheets: dto?.searchLandRegistrationDetails
    ? []
    : dto?.landRegistrationSheets ?? [],
})

/**
 * Pure — regex from the DocEstate API documentation (legacy
 * `PropertyInformationStep.context.ts:230-234`). A land-register sheet name is a
 * run of digits optionally followed by a single letter (`12`, `12A`). Copied
 * verbatim here so the module carries ZERO `PurchaseFlow/*` imports.
 */
export const isLandRegisterNameValid = (name: string): boolean => /^[0-9]+[A-Za-z]?$/.test(name)

/**
 * Pure completeness predicate — the ONLY source of truth. Distilled from legacy
 * `isPropertyInformationStepValid` (`PurchaseFlowUtilities.context.tsx:291-317`)
 * with the DEAD gates removed (spec §4a):
 *   - `isProductStepValid` (cart gate — always drops out in v2),
 *   - `isLandLordLandSheetInformationMandatory` (always false → name never
 *     mandatory, landlord never gated).
 *
 * The `searchLandRegistrationDetails` short-circuit restores legacy's
 * `searchProduct` branch: when BKBN is doing the lookup there is nothing for the
 * client to fill, so blank sheets pass. Legacy expressed it as "skip the per-sheet
 * validation", guarded by an `!landRegisterSheetItems.size` check that could never
 * fail there because its constate seeded a blank sheet on mount. v2 does not seed
 * (read-only mount), so the same rule has to short-circuit the emptiness check too
 * — same observable behaviour, one fewer phantom row.
 *
 * Predicate fidelity: legacy does NOT require `floors.length > 0` (a sheet with
 * zero floors passes — unreachable in practice as the last floor can't be
 * removed). Name is validated only WHEN PRESENT. District + every parcel number
 * are the real gates.
 */
export const propertyInfoIsComplete = (dto: PropertyInfoDraftDTO | undefined): boolean => {
  if (dto?.searchLandRegistrationDetails) return true

  const sheets = dto?.landRegistrationSheets ?? []
  if (!sheets.length) return false

  for (const sheet of sheets) {
    // Name is OPTIONAL, but if present it must match the DocEstate regex.
    if (sheet.name && !isLandRegisterNameValid(sheet.name)) return false
    if (!sheet.district) return false

    for (const floor of sheet.floors ?? []) {
      if (!floor.parcels?.length) return false
      for (const parcel of floor.parcels) {
        if (!parcel.number) return false
      }
    }
  }

  return true
}
